import { ref, onBeforeUnmount } from 'vue'

/**
 * 掃描槍輸入處理 Composable
 *
 * @param {Object} options - 配置選項
 * @param {Function} options.onScan - 掃描完成回調函數，接收掃描結果字串
 * @param {number} [options.minLength=8] - 最小掃描長度，小於此長度的輸入會被忽略
 * @param {number} [options.inputTimeout=150] - 輸入超時時間(ms)，超過此時間無新輸入則清空緩衝區
 * @returns {Object} 包含 scanBuffer, handleKeyDown, clearBuffer 等方法
 *
 * @example
 * const { scanBuffer, handleKeyDown, clearBuffer } = useScannerGun({
 *   onScan: async (scannedValue) => {
 *     console.log('Scanned:', scannedValue)
 *     // 處理掃描結果
 *   },
 *   minLength: 8,
 *   inputTimeout: 150
 * })
 */
export function useScannerGun (options = {}) {
  const {
    onScan,
    minLength = 8,
    inputTimeout: timeoutDuration = 150,
  } = options

  const scanBuffer = ref('')
  let inputTimeout = null

  /**
   * 鍵碼到字符的映射(用於處理掃描槍輸入)
   *
   * @param {string} code - 按鍵代碼
   * @param {boolean} shiftKey - 是否按下 Shift 鍵
   * @returns {string} 對應的字符
   */
  const keyCodeToChar = (code, shiftKey) => {
    // 數字鍵
    if (code.startsWith('Digit')) {
      return code.replace('Digit', '')
    }
    // 字母鍵
    if (code.startsWith('Key')) {
      const letter = code.replace('Key', '')
      return shiftKey ? letter : letter.toLowerCase()
    }
    // 數字鍵盤
    if (code.startsWith('Numpad')) {
      return code.replace('Numpad', '')
    }
    // 特殊字符
    const specialChars = {
      Minus: shiftKey ? '_' : '-',
      Equal: shiftKey ? '+' : '=',
      BracketLeft: shiftKey ? '{' : '[',
      BracketRight: shiftKey ? '}' : ']',
      Backslash: shiftKey ? '|' : '\\',
      Semicolon: shiftKey ? ':' : ';',
      Quote: shiftKey ? '"' : '\'',
      Comma: shiftKey ? '<' : ',',
      Period: shiftKey ? '>' : '.',
      Slash: shiftKey ? '?' : '/',
      Space: ' ',
    }
    return specialChars[code] || ''
  }

  /**
   * 清空掃描緩衝區和計時器
   */
  const clearBuffer = () => {
    scanBuffer.value = ''
    if (inputTimeout) {
      clearTimeout(inputTimeout)
      inputTimeout = null
    }
  }

  /**
   * 處理鍵盤按下事件(用於捕捉掃描槍輸入)
   *
   * @param {KeyboardEvent} event - 鍵盤事件
   */
  const handleKeyDown = (event) => {
    // 阻止默認行為
    event.preventDefault()

    const { code, shiftKey } = event

    // Enter 鍵 - 掃描完成
    if (code === 'Enter') {
      handleEnter()
      return
    }

    // Backspace 鍵
    if (code === 'Backspace') {
      scanBuffer.value = scanBuffer.value.slice(0, -1)
      return
    }

    // 轉換按鍵為字符
    const char = keyCodeToChar(code, shiftKey)
    if (char) {
      scanBuffer.value += char
    }

    // 重置超時計時器
    if (inputTimeout) {
      clearTimeout(inputTimeout)
    }

    // 設定新的超時,如果設定時間內沒有新輸入則視為人工輸入並清空
    inputTimeout = setTimeout(() => {
      // 超時則清空緩衝區(視為人工輸入或掃描中斷)
      scanBuffer.value = ''
    }, timeoutDuration)
  }

  /**
   * 處理 Enter 鍵(掃描完成)
   */
  const handleEnter = async () => {
    const scannedValue = scanBuffer.value.trim()

    // 清空緩衝區和計時器
    clearBuffer()

    // 檢查最小長度
    if (scannedValue.length < minLength) {
      return
    }

    // 執行回調
    if (onScan && typeof onScan === 'function') {
      await onScan(scannedValue)
    }
  }

  // 組件銷毀前清理
  onBeforeUnmount(() => {
    clearBuffer()
  })

  return {
    scanBuffer,
    handleKeyDown,
    clearBuffer,
  }
}
